// WW-167 + WW-168 (Vania 13 Oct 2026 · D-4 QA) · admin desk Ops page.
// Two buttons, each with a preview that lists proposed changes before
// an apply.  Apply is admin-only (require_admin dependency on the
// server) and insists on `confirm: true` in the body.
//
// Sign-in details are NEVER returned to this UI; the apply response
// only names the credentials file path.
import React from "react";
import { client } from "@/lib/api";
import { DuplicatePurgePanel } from "@/components/admin/DuplicatePurgePanel";
import { YearCleanupPanel } from "@/components/admin/YearCleanupPanel";
import { CountryHoldPanel } from "@/components/admin/CountryHoldPanel";
import { PreviewApplyPanel } from "@/components/admin/PreviewApplyPanel";
import { QueueExpiryPanel } from "@/components/admin/QueueExpiryPanel";
import { AiPathPanel } from "@/components/admin/AiPathPanel";

const btn = (bg, color) => ({
  padding: "10px 16px", borderRadius: 10, border: `1px solid ${bg}`,
  background: bg, color: color || "#1A1A30",
  fontFamily: "Poppins", fontWeight: 700, fontSize: 13,
  textTransform: "lowercase", cursor: "pointer", minHeight: 40,
});
const ghost = { ...btn("transparent", "var(--ww-text)"), border: "1px solid var(--ww-line-strong)" };
const yellow = btn("#FFD60A", "#1A1A30");
const red = btn("#D64545", "#FFFFFF");

export default function OpsPage() {
  return (
    <div data-testid="admin-ops-page"
         style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24,
                  background: "var(--ww-ground)", color: "var(--ww-text)",
                  minHeight: "100vh" }}>
      <h1 style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 28 }}>
        ops · live scripts
      </h1>
      <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>
        preview any job before you apply it.  apply is admin only and
        asks for confirmation.  sign in details are written to
        <code> /app/memory/test_credentials.md </code> only, never shown
        here.
      </p>
      <OpsCard
        testid="test-fleet"
        title="WW-168 · test fleet setup"
        line="creates or converts the 11 .mjb test accounts, the shared squad and 2 is_test events.  signs in details land in the credentials file only."
        previewPath="/admin/ops/test-fleet/preview"
        applyPath="/admin/ops/test-fleet/apply"
        renderRow={(r) => (
          <tr key={r.target_uid}>
            <td style={td}>@{r.handle}</td>
            <td style={td}>{r.target_uid}</td>
            <td style={td}>{r.action}</td>
            <td style={td}>{r.archetype}</td>
            <td style={td}>{r.convert_from || "(new)"}</td>
          </tr>
        )}
        header={<tr><th style={th}>handle</th><th style={th}>uid</th><th style={th}>action</th><th style={th}>archetype</th><th style={th}>source</th></tr>}
      />
      <DuplicatePurgePanel />
      <YearCleanupPanel />
      <CountryHoldPanel />
      <QueueExpiryPanel />
      <AiPathPanel />
      <PreviewApplyPanel id="unpair-repair" path="/admin/duplicates/unpair-repair" verb="unhide"
        title="WW-216 · unhide events wrongly paired as duplicates"
        note="one off. finds hidden duplicates whose venue and curator both differ from their keeper, and puts them back in the review queue. they are never paired with that keeper again."
        renderRow={(r) => <><b>{r.title || "untitled"}</b> · {r.venue || "—"} · {r.curator || "—"} · was hidden under <b>{r.keeper_title || "—"}</b> · {r.keeper_venue || "—"} · {r.keeper_curator || "—"}</>} />
    </div>
  );
}

function OpsCard({ testid, title, line, previewPath, applyPath, renderRow, header }) {
  const [rows, setRows] = React.useState(null);
  const [skipped, setSkipped] = React.useState(null);
  const [dupRows, setDupRows] = React.useState(null);
  const [extras, setExtras] = React.useState(null);
  const [err, setErr] = React.useState("");
  const [confirming, setConfirming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [applyMsg, setApplyMsg] = React.useState("");

  const runPreview = async () => {
    setErr(""); setApplyMsg(""); setBusy(true);
    try {
      const { data } = await client.post(previewPath);
      setRows(data.rows || []);
      setSkipped(data.skipped || null);
      setDupRows(data.duplicate_user_id_rows || null);
      setExtras(data.extras || null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "preview failed");
    } finally {
      setBusy(false);
    }
  };
  const runApply = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await client.post(applyPath, { confirm: true });
      setApplyMsg(
        data.credentials_file
          ? `wrote ${data.written_users || "—"} users · credentials: ${data.credentials_file}`
          : `wrote ${data.written} rows.`,
      );
      setConfirming(false);
      await runPreview();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "apply failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid={`ops-card-${testid}`}
             style={{ border: "1px solid var(--ww-line)", borderRadius: 12,
                      padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
      <h2 style={{ fontFamily: "Poppins", fontWeight: 800, fontSize: 20 }}>{title}</h2>
      <p style={{ fontFamily: "Arial", fontSize: 13, color: "var(--ww-text-muted)" }}>{line}</p>
      <div style={{ display: "flex", gap: 10 }}>
        <button data-testid={`ops-${testid}-preview`}
                onClick={runPreview} disabled={busy} style={ghost}>
          {busy && rows === null ? "loading…" : "preview"}
        </button>
        {rows && rows.length > 0 && !confirming && (
          <button data-testid={`ops-${testid}-apply-ask`}
                  onClick={() => setConfirming(true)} disabled={busy} style={yellow}>
            apply ({rows.length})
          </button>
        )}
        {confirming && (
          <>
            <button data-testid={`ops-${testid}-apply-confirm`}
                    onClick={runApply} disabled={busy} style={red}>
              yes, write {rows.length} rows
            </button>
            <button data-testid={`ops-${testid}-apply-cancel`}
                    onClick={() => setConfirming(false)} disabled={busy} style={ghost}>
              cancel
            </button>
          </>
        )}
      </div>
      {err && <p data-testid={`ops-${testid}-error`} style={{ color: "#D64545" }}>{err}</p>}
      {applyMsg && <p data-testid={`ops-${testid}-apply-msg`} style={{ color: "#4CAF50" }}>{applyMsg}</p>}
      {rows && (
        <div style={{ overflow: "auto", maxHeight: 400 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>{header}</thead>
            <tbody>{rows.map(renderRow)}</tbody>
          </table>
          {extras && (
            <pre style={{ fontFamily: "Monaco, monospace", fontSize: 11,
                          marginTop: 10, color: "var(--ww-text-muted)" }}>
{JSON.stringify(extras, null, 2)}
            </pre>
          )}
        </div>
      )}
      {skipped && skipped.length > 0 && (
        <div data-testid={`ops-${testid}-skipped`}
             style={{ marginTop: 14 }}>
          <h3 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                       textTransform: "lowercase", marginBottom: 6 }}>
            skipped · {skipped.length}
          </h3>
          <div style={{ overflow: "auto", maxHeight: 240 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>user_id</th><th style={th}>name</th>
                    <th style={th}>email</th><th style={th}>reason</th></tr>
              </thead>
              <tbody>
                {skipped.map((s, i) => (
                  <tr key={`${s.user_id}-${i}`}>
                    <td style={td}>{s.user_id}</td>
                    <td style={td}>{s.name || "—"}</td>
                    <td style={td}>{s.email || "—"}</td>
                    <td style={td}>{s.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {dupRows && dupRows.length > 0 && (
        <div data-testid={`ops-${testid}-duplicates`}
             style={{ marginTop: 14 }}>
          <h3 style={{ fontFamily: "Poppins", fontWeight: 700, fontSize: 14,
                       textTransform: "lowercase", marginBottom: 6,
                       color: "#D64545" }}>
            duplicate user_id rows · {dupRows.length} · vania to decide
          </h3>
          <div style={{ overflow: "auto", maxHeight: 240 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th style={th}>user_id</th><th style={th}>name</th>
                    <th style={th}>email</th><th style={th}>handle</th>
                    <th style={th}>created_at</th><th style={th}>is_admin</th></tr>
              </thead>
              <tbody>
                {dupRows.map((d, i) => (
                  <tr key={`${d.user_id}-${d.email || i}`}>
                    <td style={td}>{d.user_id}</td>
                    <td style={td}>{d.name || "—"}</td>
                    <td style={td}>{d.email || "—"}</td>
                    <td style={td}>{d.handle || "—"}</td>
                    <td style={td}>{d.created_at || "—"}</td>
                    <td style={td}>{String(!!d.is_admin)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

const th = { textAlign: "left", padding: "6px 10px", borderBottom: "1px solid var(--ww-line)",
             fontFamily: "Poppins", fontSize: 12, textTransform: "lowercase",
             color: "var(--ww-text-muted)" };
const td = { padding: "6px 10px", borderBottom: "1px solid var(--ww-line)",
             fontFamily: "Arial", fontSize: 13 };
